import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Copy, LogOut, ShieldCheck, Timer } from "lucide-react";
import { toast } from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import {
  clearAuthTokens,
  clearImpersonationMeta,
  getImpersonationMeta,
  type ImpersonationMeta,
} from "@/utils/auth";
import { useEndImpersonationMutation } from "@/services/authApi";
import { useConfirm } from "@/context/Confirm";

const ADMIN_APP_URL = import.meta.env.VITE_ADMIN_APP_URL || "/";
const WARN_THRESHOLD_MS = 10 * 60 * 1000;
const CRITICAL_THRESHOLD_MS = 2 * 60 * 1000;

type Severity = "default" | "warn" | "critical" | "expired";

const getSeverity = (diffMs: number): Severity => {
  if (diffMs <= 0) return "expired";
  if (diffMs <= CRITICAL_THRESHOLD_MS) return "critical";
  if (diffMs <= WARN_THRESHOLD_MS) return "warn";
  return "default";
};

const formatCountdown = (diffMs: number) => {
  if (diffMs <= 0) return "Expired";
  const totalSeconds = Math.floor(diffMs / 1000);
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hrs > 0) return `${hrs}h ${mins.toString().padStart(2, "0")}m`;
  if (mins >= 5) return `${mins}m`;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const getInitials = (name?: string, email?: string) => {
  const source = (name?.trim() || email || "").trim();
  if (!source) return "?";
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts[1]?.[0] ?? "";
  return (first + second).toUpperCase() || source[0].toUpperCase();
};

// ─── Theme maps ───────────────────────────────────────────────────────────────

type ToneMap = {
  shell: string;
  iconBg: string;
  iconRing: string;
  dot: string;
  timer: string;
  timerBorder: string;
  timerText: string;
  progress: string;
  progressGlow: string;
  exitBtn: string;
  scopeBadge: string;
};

const getTone = (severity: Severity): ToneMap => {
  if (severity === "critical" || severity === "expired") {
    return {
      shell: "border-red-200/60 bg-red-50",
      iconBg: "bg-red-500",
      iconRing: "ring-red-500/20",
      dot: "bg-red-500",
      timer: "bg-white border-red-200",
      timerBorder: "border-red-200",
      timerText: "text-red-700",
      progress: "bg-red-500",
      progressGlow: "shadow-[0_0_8px_rgba(239,68,68,0.5)]",
      exitBtn:
        "bg-red-600! text-white! border-transparent! hover:bg-red-700! shadow-xs",
      scopeBadge: "bg-red-100 text-red-700 border border-red-200/60",
    };
  }
  if (severity === "warn") {
    return {
      shell: "border-amber-200/60 bg-amber-50",
      iconBg: "bg-amber-500",
      iconRing: "ring-amber-500/20",
      dot: "bg-amber-500",
      timer: "bg-amber-50 border-amber-200",
      timerBorder: "border-amber-200",
      timerText: "text-amber-800",
      progress: "bg-amber-500",
      progressGlow: "shadow-[0_0_8px_rgba(245,158,11,0.5)]",
      exitBtn:
        "bg-amber-500! text-white! border-transparent! hover:bg-amber-600! shadow-xs",
      scopeBadge: "bg-amber-100 text-amber-700 border border-amber-200/60",
    };
  }
  return {
    shell: "border-pink-200/50 bg-muted",
    iconBg: "bg-danger",
    iconRing: "ring-danger/20",
    dot: "bg-danger",
    timer: "bg-white border-pink-200/60",
    timerBorder: "border-pink-200/60",
    timerText: "text-danger",
    progress: "bg-danger",
    progressGlow: "shadow-[0_0_8px_rgba(246,91,170,0.45)]",
    exitBtn:
      "bg-danger! text-white! border-transparent! hover:bg-danger! shadow-xs",
    scopeBadge: "bg-pink-100/80 text-danger border border-pink-200/50",
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

const ImpersonationBanner = () => {
  const { t } = useTranslation(["common"]);
  const [meta, setMeta] = useState<ImpersonationMeta | null>(() =>
    getImpersonationMeta(),
  );
  const [now, setNow] = useState(() => Date.now());
  const [endImpersonation, { isLoading }] = useEndImpersonationMutation();
  const confirm = useConfirm();
  const hasWarnedRef = useRef(false);

  // Tick interval — faster near expiry
  useEffect(() => {
    if (!meta) return;
    const tick = () => setNow(Date.now());
    const diff = new Date(meta.expiresAt).getTime() - Date.now();
    const intervalMs = diff <= WARN_THRESHOLD_MS ? 1000 : 30_000;
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [meta, now]);

  // Stay in sync across tabs
  useEffect(() => {
    const handler = () => setMeta(getImpersonationMeta());
    window.addEventListener("focus", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("focus", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const diffMs = useMemo(
    () => (meta ? new Date(meta.expiresAt).getTime() - now : 0),
    [meta, now],
  );
  const severity = getSeverity(diffMs);

  const totalMs = useMemo(() => {
    if (!meta) return 0;
    return (
      new Date(meta.expiresAt).getTime() - new Date(meta.startedAt).getTime()
    );
  }, [meta]);

  const elapsedPct = useMemo(() => {
    if (!totalMs || !meta) return 0;
    const pct = ((totalMs - Math.max(diffMs, 0)) / totalMs) * 100;
    return Math.min(100, Math.max(0, pct));
  }, [diffMs, totalMs, meta]);

  // One-shot critical toast
  useEffect(() => {
    if (severity === "critical" && !hasWarnedRef.current) {
      hasWarnedRef.current = true;
      toast.error("Impersonation session expires in under 2 minutes.", {
        id: "impersonation-expiring",
      });
    }
    if (severity !== "critical") hasWarnedRef.current = false;
  }, [severity]);

  const doExit = useCallback(async () => {
    try {
      const res = await endImpersonation().unwrap();
      const url = res?.data?.redirectUrl || ADMIN_APP_URL;
      clearAuthTokens();
      clearImpersonationMeta();
      window.location.assign(url);
    } catch {
      clearAuthTokens();
      clearImpersonationMeta();
      window.location.assign(ADMIN_APP_URL);
    }
  }, [endImpersonation]);

  const onExitClick = useCallback(() => {
    confirm({
      title: "Exit Impersonation",
      msg: "You will be signed back into the admin panel. Any unsaved changes in this session will be lost.",
      icon: <LogOut className="size-5" />,
      color: "var(--color-danger)",
      onConfirm: doExit,
    });
  }, [confirm, doExit]);

  const onCopyId = useCallback(() => {
    if (!meta) return;
    navigator.clipboard
      .writeText(meta.impersonationId)
      .then(() => toast.success("Session ID copied."))
      .catch(() => toast.error("Failed to copy."));
  }, [meta]);

  if (!meta) return null;

  const Icon = ShieldCheck;
  const scopeLabel = "Read-only";
  const adminDisplayName = meta.adminName || meta.adminEmail;
  const tone = getTone(severity);
  const isCritical = severity === "critical" || severity === "expired";

  return (
    <TooltipProvider delayDuration={150}>
      <div
        role="status"
        aria-live="polite"
        aria-label={t("common:a11y.active_impersonation_session")}
        className={cn(
          "sticky top-0 z-50 w-full shrink-0",
          "border-b shadow-xs backdrop-blur-xs",
          "transition-colors duration-300",
          tone.shell,
        )}
      >
        {/* ── Main row ── */}
        <div className="mx-auto flex w-full max-w-[1440px] items-center gap-2.5 px-3 py-2 sm:gap-3 sm:px-5">
          {/* Icon + pulse dot */}
          <div className="relative shrink-0">
            <div
              className={cn(
                "flex size-7 items-center justify-center rounded-full ring-[3px]",
                tone.iconBg,
                tone.iconRing,
              )}
            >
              <Icon className="size-3.5 text-white" aria-hidden="true" />
            </div>
            <span
              className={cn(
                "absolute -right-0.5 -top-0.5 size-2 rounded-full ring-2 ring-white",
                tone.dot,
                "animate-pulse",
              )}
              aria-hidden="true"
            />
          </div>

          {/* Label + admin info */}
          <div className="flex min-w-0 items-center gap-2 text-[12.5px]">
            <span className="shrink-0 font-semibold text-gray-800">
              {t("common:chrome.impersonating")}
            </span>

            <span
              className="size-1 shrink-0 rounded-full bg-gray-300"
              aria-hidden="true"
            />

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex min-w-0 cursor-default items-center gap-2 rounded-full bg-white/70 py-0.5 ps-0.5 pe-2.5 ring-1 ring-pink-200/60 backdrop-blur-xs">
                  {meta.adminProfileImage ? (
                    <img
                      src={meta.adminProfileImage}
                      alt={adminDisplayName}
                      className="size-6 shrink-0 rounded-full object-cover ring-2 ring-white"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display =
                          "none";
                      }}
                    />
                  ) : (
                    <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-danger to-danger text-[9px] font-bold text-white ring-2 ring-white">
                      {getInitials(meta.adminName, meta.adminEmail)}
                    </span>
                  )}
                  <span className="truncate text-[12.5px] font-semibold text-gray-800 max-w-[160px] leading-none">
                    {adminDisplayName}
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <div className="space-y-0.5">
                  {meta.adminName && (
                    <div className="font-semibold">{meta.adminName}</div>
                  )}
                  <div className="text-muted-foreground">{meta.adminEmail}</div>
                </div>
              </TooltipContent>
            </Tooltip>

            {/* Scope badge — hidden on xs */}
            <span
              className={cn(
                "hidden sm:inline-flex items-center rounded-full px-2 py-0.5",
                "text-[10px] font-semibold tracking-wide",
                tone.scopeBadge,
              )}
            >
              {scopeLabel}
            </span>
          </div>

          {/* Right controls */}
          <div className="ms-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
            {/* Countdown chip */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1",
                    "text-[11.5px] font-semibold tabular-nums select-none",
                    tone.timer,
                    tone.timerText,
                    isCritical && "animate-pulse",
                  )}
                >
                  <Timer className="size-3 shrink-0" aria-hidden="true" />
                  {formatCountdown(diffMs)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <div className="font-medium">
                  {t("common:chrome.time_remaining")}
                </div>
                <div className="text-muted-foreground">
                  {t("common:chrome.expires_at", {
                    time: new Date(meta.expiresAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  })}
                </div>
              </TooltipContent>
            </Tooltip>

            {/* Copy session ID — hidden on small screens */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onCopyId}
                  className={cn(
                    "hidden md:inline-flex size-7 items-center justify-center rounded-md",
                    "text-gray-500 transition-colors",
                    "hover:bg-black/5 hover:text-gray-900",
                    "active:bg-black/10",
                  )}
                  aria-label={t("common:a11y.copy_session_id")}
                >
                  <Copy className="size-3.5" strokeWidth={2.25} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <div className="font-medium">
                  {t("common:a11y.copy_session_id")}
                </div>
                <div className="font-mono text-muted-foreground text-[10px]">
                  {meta.impersonationId}
                </div>
              </TooltipContent>
            </Tooltip>

            {/* Exit button */}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onExitClick}
              isLoading={isLoading}
              className={cn(
                "h-7! shrink-0 rounded-lg px-2.5! text-[11.5px]! font-semibold!",
                "transition-all",
                tone.exitBtn,
              )}
            >
              <LogOut className="size-3.5" strokeWidth={2.25} />
              <span className="hidden sm:inline">{t("common:chrome.exit")}</span>
            </Button>
          </div>
        </div>

        {/* ── Progress bar ── */}
        <div
          className="h-[3px] w-full bg-black/4"
          role="progressbar"
          aria-valuenow={Math.round(elapsedPct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t("common:a11y.session_elapsed")}
        >
          <div
            className={cn(
              "h-full rounded-e-full transition-[width] duration-500 ease-out",
              tone.progress,
              tone.progressGlow,
              // Glow dot at the tip
              "relative after:absolute after:end-0 after:top-1/2 after:-translate-y-1/2",
              "after:size-1.5 after:rounded-full after:bg-inherit",
            )}
            style={{ width: `${elapsedPct}%` }}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ImpersonationBanner;
